import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { companyInfo, stats, services, testimonials } from "@/data/mock";
import { motion } from "framer-motion";
import { Network, Camera, Sun, ArrowRight, Star, Shield, Zap, Clock, ChevronRight, Play, Pause, Volume2, VolumeX } from "lucide-react";

const iconMap = { Network, Camera, Sun };

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6 }
};

export default function HomePage() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-onix-dark">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(6,182,212,0.12),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(6,182,212,0.08),transparent_60%)]" />
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(rgba(6,182,212,0.07) 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }} />
        </div>

        {/* Floating orbs */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-cyan-600/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-8">
              <Zap size={14} />
              Soluciones Tecnológicas Integrales
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-orbitron font-bold text-white leading-tight mb-6">
              Tecnología al
              <span className="block text-cyan-400 mt-2">alcance de tus manos</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-lg">
              Expertos en redes, videovigilancia y energía solar. Protegemos, conectamos y potenciamos tu hogar y negocio con las mejores soluciones del mercado.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contacto"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-onix-dark font-semibold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
              >
                Solicitar Cotización
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 px-8 py-4 border border-slate-700 text-slate-300 rounded-xl hover:border-cyan-500/40 hover:text-cyan-400 transition-all duration-300"
              >
                Ver Servicios
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-3xl scale-75" />
              <img
                src={companyInfo.logo}
                alt="Grupo Onix"
                className="relative w-80 h-80 object-contain drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-6 -mt-8 z-10">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                className="bg-onix-card/80 backdrop-blur-xl border border-white/5 rounded-2xl p-6 text-center hover:border-cyan-500/20 transition-all duration-300 group"
              >
                <div className="text-3xl md:text-4xl font-orbitron font-bold text-cyan-400 mb-2 group-hover:scale-105 transition-transform">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-24 bg-onix-darker">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-12">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Nuestro Trabajo</span>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mt-3 mb-4">
              Conectamos el <span className="text-cyan-400">futuro</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Descubre cómo nuestras soluciones en redes, seguridad y energía solar transforman hogares y empresas.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-cyan-500/5"
          >
            {/* Video Container */}
            <div className="relative aspect-video bg-onix-dark">
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                poster="https://images.pexels.com/videos/8817471/pexels-photo-8817471.jpeg?auto=compress&cs=tinysrgb&w=1260"
              >
                <source src="https://videos.pexels.com/video-files/8817471/8817471-uhd_2560_1440_30fps.mp4" type="video/mp4" />
              </video>

              {/* Overlay with gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-onix-dark/80 via-onix-dark/20 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-onix-dark/40 to-transparent" />

              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                      <span className="text-cyan-400 text-sm font-medium uppercase tracking-wider">Soluciones Tecnológicas</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-orbitron font-bold text-white mb-3">
                      Infraestructura Digital de Alto Rendimiento
                    </h3>
                    <p className="text-slate-300 text-sm md:text-base max-w-xl">
                      Redes de fibra óptica, sistemas de videovigilancia inteligente y paneles solares de última generación para conectar, proteger y energizar tu mundo.
                    </p>
                  </div>

                  {/* Video Controls */}
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={togglePlay}
                      className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300"
                      aria-label={isPlaying ? "Pausar" : "Reproducir"}
                    >
                      {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                    </button>
                    <button
                      onClick={toggleMute}
                      className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300"
                      aria-label={isMuted ? "Activar sonido" : "Silenciar"}
                    >
                      {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Corner accent */}
              <div className="absolute top-6 right-6">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-onix-dark/60 backdrop-blur-sm border border-cyan-500/20">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-cyan-400 text-xs font-medium">GRUPO ONIX</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Service highlights below video */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {[
              { icon: Network, label: "Redes & Fibra Óptica", desc: "Conectividad de alta velocidad" },
              { icon: Camera, label: "CCTV & Seguridad", desc: "Vigilancia inteligente 24/7" },
              { icon: Sun, label: "Energía Solar", desc: "Ahorro y sostenibilidad" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-all duration-300 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/20 transition-colors">
                  <item.icon size={22} className="text-cyan-400" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">{item.label}</div>
                  <div className="text-slate-500 text-xs">{item.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Nuestros Servicios</span>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mt-3 mb-4">
              Soluciones que <span className="text-cyan-400">transforman</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Ofrecemos servicios especializados en tres áreas clave para cubrir todas tus necesidades tecnológicas.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, i) => {
              const Icon = iconMap[service.icon];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <div className="group h-full bg-onix-card border border-white/5 rounded-2xl overflow-hidden hover:border-cyan-500/20 transition-all duration-500">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-onix-card via-transparent to-transparent" />
                      <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-onix-dark/80 backdrop-blur-sm border border-cyan-500/20 flex items-center justify-center">
                        {Icon && <Icon size={22} className="text-cyan-400" />}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-slate-400 text-sm leading-relaxed mb-5">
                        {service.shortDesc}
                      </p>
                      <Link
                        to="/servicios"
                        className="inline-flex items-center gap-2 text-cyan-400 text-sm font-medium group-hover:gap-3 transition-all"
                      >
                        Conocer más <ChevronRight size={16} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 bg-onix-darker">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">¿Por qué elegirnos?</span>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mt-3">
              La diferencia <span className="text-cyan-400">Onix</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Garantía Total", desc: "Respaldamos cada proyecto con garantía completa en materiales e instalación." },
              { icon: Zap, title: "Tecnología de Punta", desc: "Utilizamos equipos y materiales de las mejores marcas del mercado." },
              { icon: Clock, title: "Entrega a Tiempo", desc: "Cumplimos con los plazos establecidos sin comprometer la calidad." },
              { icon: Star, title: "Soporte 24/7", desc: "Asistencia técnica disponible las 24 horas, los 7 días de la semana." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/20 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5 group-hover:bg-cyan-500/20 transition-colors">
                  <item.icon size={22} className="text-cyan-400" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Testimonios</span>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mt-3">
              Lo que dicen nuestros <span className="text-cyan-400">clientes</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="p-6 rounded-2xl bg-onix-card border border-white/5 hover:border-cyan-500/15 transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} size={16} className="fill-cyan-400 text-cyan-400" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div>
                  <div className="text-white font-medium text-sm">{t.name}</div>
                  <div className="text-slate-500 text-xs">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-onix-darker">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold text-white mb-6">
              ¿Listo para transformar tu espacio?
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
              Contáctanos hoy y recibe una cotización personalizada sin compromiso. Nuestro equipo de expertos está listo para ayudarte.
            </p>
            <Link
              to="/contacto"
              className="group inline-flex items-center gap-2 px-10 py-4 bg-cyan-500 text-onix-dark font-bold rounded-xl hover:bg-cyan-400 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25 text-lg"
            >
              Contáctanos Ahora
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
